import type {
  GetCalendarDataQuery,
  GetMailingCalendarQuery,
  GetStatusWithActionsQuery,
  GetTagsByCategoryNameQuery,
  MailingsByDeadlineQuery,
  PagedBlueprintQuery,
  PagedMailingListQuery,
  PagedMailingQuery,
  PagedRecipientPreviewQuery,
  PagedRecipientQuery,
  PagedTemplateDetailsByNameQuery,
  State,
  TemplateCategoryQuery,
  TemplateQuery,
} from '@enterprise/api';

const baseKey = ['datastream'] as const;

/**
 * Query key definitions for datastream-related queries.
 *
 * @remarks
 * This object provides structured query keys for use with React Query or similar libraries,
 * ensuring consistent cache keys for various datastream features.
 */
export const datastreamQueryKeys = {
  auth: {
    base: () => {
      return [...datastreamQueryKeys.base, 'auth'] as const;
    },
    permission: (permissions: string[]) => {
      return [...datastreamQueryKeys.auth.base(), 'permission', ...permissions] as const;
    },
  },
  base: baseKey,
  blueprints: {
    base: [...baseKey, 'blueprints'] as const,
    byId: (id: number) => {
      return [...datastreamQueryKeys.blueprints.base, 'by-id', id] as const;
    },
    paged: {
      base: () => {
        return [...datastreamQueryKeys.blueprints.base, 'page'] as const;
      },
      withQuery: (query: PagedBlueprintQuery) => {
        return [...datastreamQueryKeys.blueprints.paged.base(), { ...query }] as const;
      },
    },
  },
  calendar: {
    base: [...baseKey, 'calendar'] as const,
    getData: (query: GetCalendarDataQuery, isExternal = false) => {
      return [...datastreamQueryKeys.calendar.base, 'counts', isExternal, { ...query }] as const;
    },
    getMailingCalendar: (query: GetMailingCalendarQuery, isExternal = false) => {
      return [...datastreamQueryKeys.calendar.base, isExternal, { ...query }] as const;
    },
  } as const,
  lookup: {
    agencies: (search?: string) => {
      return [...datastreamQueryKeys.lookup.base, 'agencies', { search }] as const;
    },
    base: [...baseKey, 'lookup'] as const,
    clients: (search?: string) => {
      return [...datastreamQueryKeys.lookup.base, 'clients', { search }] as const;
    },
    customers: (search?: string) => {
      return [...datastreamQueryKeys.lookup.base, 'customers', { search }] as const;
    },
    districtsByState: (state: State[]) => {
      return [...datastreamQueryKeys.lookup.base, 'districts', { state }] as const;
    },
    fileTypes: () => {
      return [...datastreamQueryKeys.lookup.base, 'file-types'] as const;
    },
    states: () => {
      return [...datastreamQueryKeys.lookup.base, 'states'] as const;
    },
  },
  mailing: {
    base: [...baseKey, 'mailing'] as const,
    byDeadline: {
      base: () => {
        return [...datastreamQueryKeys.mailing.base, 'by-deadline'] as const;
      },
      // The lane is part of the key, so each lane's scroll pages cache independently while every
      // lane still invalidates together when the filters change.
      lane: (query: MailingsByDeadlineQuery, lane: number, isExternal = false) => {
        return [...datastreamQueryKeys.mailing.byDeadline.base(), isExternal, lane, { ...query }] as const;
      },
      withQuery: (query: MailingsByDeadlineQuery, isExternal = false) => {
        return [...datastreamQueryKeys.mailing.byDeadline.base(), isExternal, { ...query }] as const;
      },
    },
    comments: {
      base: () => {
        return [...datastreamQueryKeys.mailing.base, 'comments'] as const;
      },
      byMailing: (mailingId: number) => {
        return [...datastreamQueryKeys.mailing.comments.base(), mailingId] as const;
      },
    },
    config: {
      base: () => {
        return [...datastreamQueryKeys.mailing.base, 'config'] as const;
      },
      byMailingId: (mailingId: number) => {
        return [...datastreamQueryKeys.mailing.config.base(), mailingId] as const;
      },
    },
    dataProcessors: {
      base: () => {
        return [...datastreamQueryKeys.mailing.base, 'data-processors'] as const;
      },
    },
    dupeGroups: {
      base: [...baseKey, 'mailing', 'dupe-groups'] as const,
      mailing: (mailingId: number) => {
        return [...datastreamQueryKeys.mailing.dupeGroups.base, mailingId] as const;
      },
      mailingList: (mailingId: number) => {
        return [...datastreamQueryKeys.mailing.dupeGroups.base, 'mailing-list', mailingId] as const;
      },
    },
    lists: {
      base: () => {
        return [...datastreamQueryKeys.mailing.base, 'lists'] as const;
      },
      comments: {
        base: () => {
          return [...datastreamQueryKeys.mailing.lists.base(), 'comments'] as const;
        },
        byMailingList: (mailingListId: number) => {
          return [...datastreamQueryKeys.mailing.comments.base(), mailingListId] as const;
        },
      },
      managers: (search?: string) => {
        return [...datastreamQueryKeys.mailing.lists.base(), 'managers', { search }] as const;
      },
      paged: {
        base: () => {
          return [...datastreamQueryKeys.mailing.lists.base(), 'page'] as const;
        },
        byMailing: {
          withQuery: (query: PagedMailingListQuery) => {
            return [...datastreamQueryKeys.mailing.lists.paged.base(), 'by-mailing', { ...query }] as const;
          },
          withQueryExternal: (query: PagedMailingListQuery) => {
            return [...datastreamQueryKeys.mailing.lists.paged.base(), 'by-mailing', 'external', { ...query }] as const;
          },
        },
        withQuery: (query: PagedMailingListQuery) => {
          return [...datastreamQueryKeys.mailing.lists.paged.base(), { ...query }] as const;
        },
        withQueryExternal: (query: PagedMailingListQuery) => {
          return [...datastreamQueryKeys.mailing.lists.paged.base(), 'external', { ...query }] as const;
        },
      },
      recipientPaged: (query: PagedRecipientPreviewQuery) => {
        return [...datastreamQueryKeys.mailing.lists.base(), 'page', { ...query }] as const;
      },
      summary: (mailingId: number) => {
        return [...datastreamQueryKeys.mailing.lists.base(), 'summary', mailingId] as const;
      },
      tags: {
        base: () => {
          return [...datastreamQueryKeys.mailing.lists.base(), 'tags'] as const;
        },
        byMailingListId: (mailingListId: number) => {
          return [...datastreamQueryKeys.mailing.lists.tags.base(), mailingListId] as const;
        },
      },
    },
    omits: {
      base: () => {
        return [...datastreamQueryKeys.mailing.base, 'omits'] as const;
      },
      byMailingId: (mailingId: number) => {
        return [...datastreamQueryKeys.mailing.omits.base(), mailingId] as const;
      },
    },
    paged: {
      base: () => {
        return [...datastreamQueryKeys.mailing.base, 'page'] as const;
      },
      withQuery: (query: PagedMailingQuery) => {
        return [...datastreamQueryKeys.mailing.paged.base(), { ...query }] as const;
      },
      withQueryExternal: (query: PagedMailingQuery) => {
        return [...datastreamQueryKeys.mailing.paged.base(), 'external', { ...query }] as const;
      },
    },
    recipientTemplateFiles: {
      base: () => {
        return [...datastreamQueryKeys.mailing.base, 'recipient-template-files'] as const;
      },
      byMailingId: (mailingId: number) => {
        return [...datastreamQueryKeys.mailing.recipientTemplateFiles.base(), mailingId] as const;
      },
    },
    scoringSummary: {
      base: () => {
        return [...datastreamQueryKeys.mailing.base, 'scoring'] as const;
      },
      byMailingId: (mailingId: number) => {
        return [...datastreamQueryKeys.mailing.scoringSummary.base(), mailingId] as const;
      },
    },
    settings: {
      base: () => {
        return [...datastreamQueryKeys.mailing.base, 'settings'] as const;
      },
      byMailingId: (mailingId?: number) => {
        return [...datastreamQueryKeys.mailing.settings.base(), mailingId] as const;
      },
      updateByMailingId: (mailingId: number) => {
        return [...datastreamQueryKeys.mailing.settings.base(), mailingId, 'update'] as const;
      },
    },
    tags: {
      base: () => {
        return [...datastreamQueryKeys.mailing.base, 'tags'] as const;
      },
      byMailingId: (mailingId: number) => {
        return [...datastreamQueryKeys.mailing.tags.base(), mailingId] as const;
      },
    },
  },
  recipients: {
    base: [...baseKey, 'recipients'] as const,
    flags: () => {
      return [...datastreamQueryKeys.recipients.base, 'flags'] as const;
    },
    paged: {
      base: () => {
        return [...datastreamQueryKeys.recipients.base, 'page'] as const;
      },
      withQuery: (query: PagedRecipientQuery) => {
        return [...datastreamQueryKeys.recipients.paged.base(), { ...query }] as const;
      },
    },
  },
  status: {
    base: [...baseKey, 'status'] as const,
    byId: (statusId: number) => {
      return [...datastreamQueryKeys.status.base, 'by-id', statusId] as const;
    },
    withActions: (query: GetStatusWithActionsQuery) => {
      return [...datastreamQueryKeys.status.base, 'with-actions', { ...query }] as const;
    },
  },
  tags: {
    base: [...baseKey, 'tags'] as const,
    byCategoryName: (query: GetTagsByCategoryNameQuery) => {
      return [...datastreamQueryKeys.tags.base, 'by-category-name', { ...query }] as const;
    },
  } as const,
  template: {
    base: [...baseKey, 'template'] as const,
    byIdOrName: (query: TemplateQuery) => {
      return [...datastreamQueryKeys.template.base, 'by-id-or-name', query] as const;
    },
    categories: {
      base: () => {
        return [...datastreamQueryKeys.template.base, 'categories'] as const;
      },
      withQuery: (query: TemplateCategoryQuery) => {
        return [...datastreamQueryKeys.template.categories.base(), { ...query }] as const;
      },
    },
    paged: {
      base: () => {
        return [...datastreamQueryKeys.template.base, 'page'] as const;
      },
      withQuery: ({ templateName, ...query }: PagedTemplateDetailsByNameQuery) => {
        return [...datastreamQueryKeys.template.paged.base(), templateName, { ...query }] as const;
      },
    },
    type: {
      base: () => {
        return [...datastreamQueryKeys.template.base, 'types'] as const;
      },
    },
    withBatches: {
      base: () => {
        return [...datastreamQueryKeys.template.base, 'with-batches'] as const;
      },
      withQuery: (query: TemplateQuery) => {
        return [...datastreamQueryKeys.template.withBatches.base(), query] as const;
      },
    },
  },
  tooltip: {
    base: [...baseKey, 'tooltip'] as const,
    byName: (tooltipName: string) => {
      return [...datastreamQueryKeys.tooltip.base, 'by-name', tooltipName] as const;
    },
  },
} as const;
